// A stream takes a BufferSource, which is the one view a shared buffer cannot be behind.
type Bytes = Uint8Array<ArrayBuffer>;

// The browser's own zlib, so nothing here implements DEFLATE and nothing is fetched to reach it. The order is the
// order a body is tried in: the two wrappers carry a checksum and can say for themselves that they were the right
// one, and raw has nothing to be recognised by, so it goes last.
const WRAPPERS: { variant: string; stream: CompressionFormat }[] = [
  { variant: "zlib", stream: "deflate" },
  { variant: "gzip", stream: "gzip" },
  { variant: "raw", stream: "deflate-raw" },
];

export async function deflate(bytes: Bytes, variant: string): Promise<Uint8Array> {
  return through(bytes, new CompressionStream(wrapper(variant).stream));
}

// The variant shapes what is written and not what is read, a SAMLRequest being raw deflate whatever the box says.
export async function inflate(bytes: Bytes, variant: string): Promise<Uint8Array> {
  const chosen = wrapper(variant);
  for (const { stream } of [chosen, ...WRAPPERS.filter((entry) => entry !== chosen)]) {
    try {
      return await through(bytes, new DecompressionStream(stream));
    } catch {
      // The next wrapper, and the error below once none of them is left.
    }
  }
  throw new Error("Input is not zlib, gzip or raw deflate data");
}

function wrapper(variant: string): { variant: string; stream: CompressionFormat } {
  return WRAPPERS.find((entry) => entry.variant === variant) ?? WRAPPERS[0];
}

// Piping owns the backpressure that writing the whole input through a writer of one's own would deadlock on.
async function through(bytes: Bytes, transform: TransformStream<BufferSource, Uint8Array>): Promise<Uint8Array> {
  const source = new ReadableStream<BufferSource>({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });

  const reader = source.pipeThrough(transform).getReader();
  const chunks: Uint8Array[] = [];
  let length = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    length += value.length;
  }

  const out = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}
